import { useEffect, useRef, useState } from "react";

const CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

interface TimerState {
  /** Formatted display string e.g. "08:42" or "+02:15" */
  display: string;
  /** Whether the timer is overdue (past 10 minutes) */
  isOverdue: boolean;
  /** Total elapsed ms since last check */
  elapsedMs: number;
}

/**
 * Countdown timer hook for sleep checks.
 * Counts down from 10:00 after a check. At 00:00, turns overdue and counts up.
 */
export function useSleepCheckTimer(
  lastCheckTimestamp: Date | null,
  isSessionActive: boolean,
): TimerState | null {
  const [now, setNow] = useState(() => Date.now());
  const prevTimestampRef = useRef<number | null>(null);

  // Reset `now` immediately when lastCheckTimestamp changes
  // so we don't show a stale elapsed value on the first render
  const timestampMs = lastCheckTimestamp?.getTime() ?? null;
  if (timestampMs !== prevTimestampRef.current) {
    prevTimestampRef.current = timestampMs;
    setNow(Date.now());
  }

  useEffect(() => {
    if (!lastCheckTimestamp || !isSessionActive) return;

    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, [lastCheckTimestamp, isSessionActive]);

  if (!lastCheckTimestamp || !isSessionActive) {
    return null;
  }

  // Clamp to 0 to handle minor server/client clock drift
  const elapsedMs = Math.max(0, now - lastCheckTimestamp.getTime());
  const isOverdue = elapsedMs >= CHECK_INTERVAL_MS;

  let display: string;
  if (isOverdue) {
    const overdueMs = elapsedMs - CHECK_INTERVAL_MS;
    const mins = Math.floor(overdueMs / 60000);
    const secs = Math.floor((overdueMs % 60000) / 1000);
    display = `+${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  } else {
    // Use ceil so the display starts at 10:00 and ticks down on each full second
    const remainingSecs = Math.ceil((CHECK_INTERVAL_MS - elapsedMs) / 1000);
    const mins = Math.floor(remainingSecs / 60);
    const secs = remainingSecs % 60;
    display = `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }

  return { display, isOverdue, elapsedMs };
}
